import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OpportunitiesTable = ({ opportunities, onViewDetails, onSort }) => {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    onSort(field, newDirection);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'qualified': return 'bg-primary/10 text-primary';
      case 'proposal': return 'bg-warning/10 text-warning';
      case 'negotiation': return 'bg-accent/10 text-accent';
      case 'won': return 'bg-success/10 text-success';
      case 'lost': return 'bg-error/10 text-error';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getRoleColor = (role) => {
    return role === 'Lead' ? 'bg-primary/10 text-primary' : 'bg-secondary/10 text-secondary';
  };

  const getOwnershipStyle = (ownedBy) => {
    if (ownedBy === 'Partner') {
      return { label: '🤝 Partner-Led', className: 'bg-purple-100 text-purple-700 border border-purple-200' };
    }
    return { label: '🏢 eCity-Led', className: 'bg-blue-100 text-blue-700 border border-blue-200' };
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-muted/50 border-b border-border">
          <tr>
            <th className="text-left p-12 md:p-16">
              <button
                onClick={() => handleSort('name')}
                className="flex items-center gap-8 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
              >
                Opportunity Name
                <Icon name={sortField === 'name' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
              </button>
            </th>
            <th className="text-left p-12 md:p-16">
              <button
                onClick={() => handleSort('sector')}
                className="flex items-center gap-8 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
              >
                Sector
                <Icon name={sortField === 'sector' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
              </button>
            </th>
            <th className="text-left p-12 md:p-16">
              <button
                onClick={() => handleSort('value')}
                className="flex items-center gap-8 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
              >
                Value
                <Icon name={sortField === 'value' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
              </button>
            </th>
            <th className="text-left p-12 md:p-16">
              <span className="text-sm font-semibold text-foreground">Engagement Role</span>
            </th>
            <th className="text-left p-12 md:p-16">
              <span className="text-sm font-semibold text-foreground">Owner</span>
            </th>
            <th className="text-left p-12 md:p-16">
              <span className="text-sm font-semibold text-foreground">Status</span>
            </th>
            <th className="text-left p-12 md:p-16">
              <button
                onClick={() => handleSort('winProbability')}
                className="flex items-center gap-8 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
              >
                Win Probability
                <Icon name={sortField === 'winProbability' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={16} />
              </button>
            </th>
            <th className="text-right p-12 md:p-16">
              <span className="text-sm font-semibold text-foreground">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {opportunities?.map((opportunity) => {
            const ownership = getOwnershipStyle(opportunity?.ownedBy);
            return (
              <tr key={opportunity?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="p-12 md:p-16">
                  <div className="font-medium text-foreground">{opportunity?.name}</div>
                  <div className="text-xs text-muted-foreground caption mt-4">{opportunity?.expectedClose}</div>
                </td>
                <td className="p-12 md:p-16">
                  <span className="text-sm text-muted-foreground">{opportunity?.sector}</span>
                </td>
                <td className="p-12 md:p-16">
                  <span className="text-sm font-medium text-foreground data-text">{opportunity?.value}</span>
                </td>
                <td className="p-12 md:p-16">
                  <span className={`inline-flex items-center px-8 py-4 rounded-md text-xs font-medium caption ${getRoleColor(opportunity?.role)}`}>
                    {opportunity?.role}
                  </span>
                </td>
                <td className="p-12 md:p-16">
                  <span className={`inline-flex items-center px-8 py-4 rounded-md text-xs font-semibold caption ${ownership?.className}`}>
                    {ownership?.label}
                  </span>
                </td>
                <td className="p-12 md:p-16">
                  <span className={`inline-flex items-center px-8 py-4 rounded-md text-xs font-medium caption ${getStatusColor(opportunity?.status)}`}>
                    {opportunity?.status}
                  </span>
                </td>
                <td className="p-12 md:p-16">
                  <div className="flex items-center gap-8">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden min-w-[60px]">
                      <div 
                        className="h-full bg-primary transition-smooth"
                        style={{ width: `${opportunity?.winProbability}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-foreground data-text whitespace-nowrap">{opportunity?.winProbability}%</span>
                  </div>
                </td>
                <td className="p-12 md:p-16 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    iconSize={16}
                    onClick={() => onViewDetails(opportunity)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default OpportunitiesTable;